// Globals
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  changeOrderStatus,
  completeOrder,
  deleteOrderFiles,
  selectOrder,
  selectOrdersIsLoading,
  updateAppraiserOrder,
} from '../../feature';

// Components
import { ConfirmationModal, useForm } from '@/components';
import { Button } from '@/ui';
import { AppraiserDetails, StyledTabs } from '../../components';
import { StyledContainer, StyledFooter } from './AppraiserDetailsContainerStyles';

// Modules
import { LogsListContainer } from '@/modules/Logs';

// Models
import { FileVariant, IFile } from '@/models/file.model';
import { EntityType } from '@/models/logs.model';
import { OrderStatus } from '@/models/order.model';
import { FileType } from '@/models/uploads.model';

// Helpers
import { getEntityId } from '@/utils';
import { useUploadOrderFiles } from '../../hooks';
import { TUploadConditionReportFields } from '../../helpers';
import UpdateAppraiserStepDTO from '../../dtos/UpdateAppraiserOrderDTO';

const AppraiserDetailsContainer = () => {
  const { t } = useTranslation('order');
  const [deleteFile, setDeleteFile] = useState<IFile | null>(null);
  const dispatch = useAppDispatch();
  const isLoading = useAppState(selectOrdersIsLoading);
  const [form] = useForm();

  const order = useAppState(selectOrder);
  const { isUploading, uploadFiles } = useUploadOrderFiles();

  const handleChangeStatus = (status: OrderStatus) =>
    dispatch(changeOrderStatus({ id: getEntityId(order?.PK ?? ''), status }));

  const handleUploadFile = async (value: File[]) => {
    if (!value?.length) return;

    const files = value.map((file) => ({ file, type: FileType.FILE }));
    uploadFiles({ orderId: getEntityId(order?.PK ?? ''), files, type: FileVariant.DOCUMENT_APPRAISER });
  };

  const handleCompleteOrder = () => dispatch(completeOrder({ id: getEntityId(order?.PK ?? '') }));

  const handleSubmit = async (values?: TUploadConditionReportFields) => {
    try {
      // Validate form before submitting
      await form.validateFields();

      // If validation passes, update the order with form values first
      if (values) {
        const dto = new UpdateAppraiserStepDTO({ ...values, id: order?.PK ?? '' });
        await dispatch(updateAppraiserOrder(dto)).unwrap();
      }

      // Then complete the order
      await handleCompleteOrder();
    } catch (error) {
      // Validation failed or API error - form will show validation errors automatically
      console.error('Form validation failed or API error:', error);
    }
  };

  const onValuesChange = useCallback(
    debounce(async (values: TUploadConditionReportFields) => {
      const dto = new UpdateAppraiserStepDTO({ ...values, id: order?.PK ?? '' });

      await dispatch(updateAppraiserOrder(dto));
    }, 300),
    [],
  );

  const handlePreview = (file: IFile) => window.open(file.link, '_blank');

  const handleDelete = async () => {
    if (!deleteFile?.id) return;
    await dispatch(
      deleteOrderFiles({
        orderId: getEntityId(order?.PK ?? ''),
        fileId: deleteFile.id,
      }),
    );
    setDeleteFile(null);
  };

  const handleUploadPhoto = async (value: File[], type: FileVariant) => {
    if (!value?.length) return;
    const files = value.map((file) => ({ file, type: FileType.IMAGE }));
    uploadFiles({ orderId: getEntityId(order?.PK ?? ''), files, type });
  };

  const handleCancel = () => setDeleteFile(null);

  if (!order) return null;

  return (
    <StyledTabs
      destroyInactiveTabPane
      items={[
        {
          key: 'details',
          label: t('details'),
          children: (
            <>
              <StyledContainer>
                <AppraiserDetails
                  order={order}
                  onChangeStatus={handleChangeStatus}
                  isLoading={isLoading || isUploading}
                  onFileUpload={handleUploadFile}
                  onPreview={handlePreview}
                  onValuesChange={onValuesChange}
                  formInstance={form}
                  onSubmit={handleSubmit}
                  onDelete={setDeleteFile}
                  handleUploadPhoto={handleUploadPhoto}
                />
              </StyledContainer>
              {!!order?.appraisal?.files?.length && order?.status === OrderStatus.IN_PROGRESS && (
                <StyledFooter>
                  <Button type="primary" shape="round" htmlType={'submit'} form={'report'}>
                    {t('send_document', { count: order?.appraisal?.files?.length ?? 0 })}
                  </Button>
                </StyledFooter>
              )}
              <ConfirmationModal
                title={t('delete_file_title')}
                isOpened={!!deleteFile?.id}
                onConfirm={handleDelete}
                onCancel={handleCancel}
                okText={t('button_delete')}
                cancelText={t('button_cancel')}
              />
            </>
          ),
        },
        {
          key: 'logs',
          label: t('logs'),
          children: <LogsListContainer id={order?.PK ?? ''} entityType={EntityType.ORDER} />,
        },
      ]}
    />
  );
};

export default AppraiserDetailsContainer;
